// src/features/statistics/entity-breakdowns.tsx
import { Kicker } from "@/components/iris/kicker";
import type { StatisticsSnapshot } from "@/lib/types";
import {
  CHART_SEMANTIC,
  ChartCard,
  DonutWithLegend,
  SimpleBar,
  chartColor,
} from "./chart-primitives";
import {
  BY_STATUS_CENTER_LABEL,
  BY_STATUS_INFO,
  byStatusSubtitle,
  locationCoverageSubtitle,
} from "./entity-breakdowns.helpers";

interface EntityBreakdownsProps {
  breakdowns: StatisticsSnapshot["entity_breakdowns"];
  totalEntities?: number;
}

export function EntityBreakdowns({ breakdowns, totalEntities }: EntityBreakdownsProps) {
  // IR-1046 ⑤ fixed the numerator here (it had summed only the top-20 bars) and
  // anchored the denominator to the ALL-STATUS total so this line agreed with
  // the By Status donut beside it. IR-1414 r2 re-anchors both to the LIVE
  // population instead: agreeing with By Status meant disagreeing with the
  // Total Entities tile and the By Platform donut, which is the "1.3M" Tomer
  // reopened the ticket over. See entity-breakdowns.helpers.ts.
  const countrySub = locationCoverageSubtitle(breakdowns, totalEntities);
  const statusSub = byStatusSubtitle(breakdowns);

  return (
    <>
      <Kicker style={{ marginTop: 32, marginBottom: 12 }}>Entity Distribution</Kicker>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
        {/* By Platform — Donut + side legend with exact counts (IR-1046 ④) */}
        <ChartCard
          title="By Platform"
          info="Profiler-originated sources (truecaller, youtube, …) are grouped under one 'profiler' slice; the long tail collapses into 'Other'."
        >
          <DonutWithLegend
            data={breakdowns.by_platform}
            nameKey="platform"
            dataKey="count"
            totalLabel="Entities"
          />
        </ChartCard>

        {/* By Country — Horizontal bar chart, top 20 (cyan for info) */}
        <ChartCard title="By Country (Top 20)" {...(countrySub ? { subtitle: countrySub } : {})}>
          <SimpleBar
            data={breakdowns.by_country}
            xKey="country"
            yKey="count"
            layout="vertical"
            color={CHART_SEMANTIC.info}
            leftMargin={120}
            tickFontSize={10}
          />
        </ChartCard>

        {/* By Status — Donut + side legend with exact counts (IR-1046 ④).
            IR-1414 r2: the only card on this page that counts deleted
            entities, so its centre total is labelled by population rather
            than "Entities" — which read as contradicting the 1.1M tile. */}
        <ChartCard
          title="By Status"
          info={BY_STATUS_INFO}
          {...(statusSub ? { subtitle: statusSub } : {})}
        >
          <DonutWithLegend
            data={breakdowns.by_status}
            nameKey="status"
            dataKey="count"
            totalLabel={BY_STATUS_CENTER_LABEL}
          />
        </ChartCard>

        {/* By Classification — Vertical bar chart (orange for warn/classification) */}
        <ChartCard title="By Classification">
          <SimpleBar
            data={breakdowns.by_classification}
            xKey="classification"
            yKey="count"
            color={chartColor(4)}
          />
        </ChartCard>

        {/* By Risk Score — Histogram with 5 buckets (blue for risk) */}
        <ChartCard title="By Risk Score">
          <SimpleBar
            data={breakdowns.by_risk_score}
            xKey="bucket"
            yKey="count"
            color={CHART_SEMANTIC.risk}
          />
        </ChartCard>
      </div>
    </>
  );
}
